import React, { useState } from 'react';
import * as Icon from 'react-bootstrap-icons';
import { Form } from 'react-bootstrap';

import { useTranslation } from '../../../common/hooks/useTranslation';
import {
  GetDictionaryDto,
  GetUmpPositionDto,
  ProfileSchemaDto,
} from '../../../common/pokCore/autogenerated/pokApiClient';
import Button from '../../../common/components/Button/Button';
import ConfirmationButton from '../../../app/components/ConfirmationButton';

import { UmpPositionsGrid } from './UmpPositionsGrid';
import {
  UmpNewPathPhase,
  UmpNewSpecialistTeam,
} from './UmpParameterMapperForm';

interface UmpPositionsListProps {
  data: GetUmpPositionDto[];
  onBack: () => void;
  onImport: (selectedKeys: string[], isPurchaseSameAsSales: boolean) => void;
  profileSchema: ProfileSchemaDto;
  companyId?: string;
  pathPhaseDictionaries?: GetDictionaryDto[];
  pathPhaseEnabled?: boolean;
  updateParams?: (
    uid: string,
    newPathPhaseDict: UmpNewPathPhase | undefined,
    newSpecialistTeam: UmpNewSpecialistTeam | undefined,
  ) => void;
}

export const UmpPositionsList: React.FC<UmpPositionsListProps> = props => {
  const { t, tk } = useTranslation('administrationLng');
  const [selectedPositions, setSelectedPositions] = useState<string[]>([]);
  const [isPurchaseSameAsSales, setIsPurchaseSameAsSales] = useState(false);

  const handleOnRowClick = (key: string) => {
    let sel: string[];
    if (!selectedPositions.includes(key)) {
      sel = [...selectedPositions, key];
    } else {
      sel = selectedPositions.filter(o => o !== key);
    }
    setSelectedPositions(sel);
  };

  const onImport = async () => {
    props.onImport(selectedPositions, isPurchaseSameAsSales);
  };

  const selectAll = () => {
    setSelectedPositions(props.data.map(p => p.uid));
  };

  const deselectAll = () => {
    setSelectedPositions([]);
  };

  return (
    <>
      <div className="side-by-side">
        <div className="left d-flex align-items-center gap-2 mb-2 mt-2">
          <Form.Check
            type="checkbox"
            checked={isPurchaseSameAsSales}
            onChange={e => setIsPurchaseSameAsSales(e.target.checked)}
          />
          <span>{t(tk.umpImport.purchaseSameAsSales)}</span>
        </div>
      </div>
      <UmpPositionsGrid
        data={props.data}
        selectedKeys={selectedPositions}
        onRowClick={handleOnRowClick}
        isPurchaseSameAsSales={isPurchaseSameAsSales}
        profileSchema={props.profileSchema}
        companyId={props.companyId}
        pathPhaseDictionaries={props.pathPhaseDictionaries}
        pathPhaseEnabled={props.pathPhaseEnabled}
        updateParams={props.updateParams}
      />
      <section className="side-by-side">
        {selectedPositions.length !== props.data.length ? (
          <Button
            variant="outline-secondary"
            onClick={selectAll}
            className="mt-1"
          >
            <Icon.CheckSquare className="icon mx-2 mb-1" />
            {t(tk.component.selectAll)}
          </Button>
        ) : (
          <Button
            variant="outline-secondary"
            onClick={deselectAll}
            className="mt-1"
          >
            <Icon.Square className="icon mx-2 mb-1" />
            {t(tk.component.deselectAll)}
          </Button>
        )}
        <div className="marginLeft">
          <ConfirmationButton
            onOK={() => onImport()}
            confirmation={t(tk.common.askImportSelected)}
            className="mt-1"
          >
            {t(tk.common.importSelected)}
          </ConfirmationButton>
        </div>
      </section>
    </>
  );
};
